<script setup lang="ts">
import {SessionState, type Session} from '@/ts/types/dto/interventions-dto';
import {useI18n} from 'vue-i18n';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import {computed} from 'vue';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import {useSessionsLinksId} from '@/ts/composables/stateful/use-links';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import CompletedProgress from '@/vue/atoms/completed-progress.vue';
import RouterLinkWithOrigin from '@/vue/atoms/router-link-with-origin.vue';
import SessionUnlocks from '@/vue/molecules/session-summary/session-unlocks.vue';

const {t: $t} = useI18n();

const props = defineProps<{ session: Session, index: number }>();

const sessionId = computed(() => props.session.id);
const {completedPercent, inProgress} = useSessionId(sessionId);
const {summaryLink} = useSessionsLinksId(sessionId);

const theme = computed(() => {
  if (props.session.state === SessionState.UNLOCK) {
    return 'blue';
  }
  if (props.session.state === SessionState.COMPLETED) {
    return 'green';
  }
  if (props.session.timeUntilUnlockMillis && props.session.timeUntilUnlockMillis > 0) {
    return 'blue';
  }
  return 'grey';
});

const SessionStateEnum = SessionState;

const isClickable = computed(() => {
  return props.session.state === SessionStateEnum.COMPLETED || props.session.state === SessionStateEnum.UNLOCK;
});

</script>

<template>
  <!-- Only wrap in router-link-with-origin if session is clickable -->
  <router-link-with-origin
    v-if="isClickable"
    :to="summaryLink"
    :origin-page-title="session?.name || ''"
  >
    <div class="session" :class="theme">
      <lazy-image :src="session!.image" :width="64" :height="64" class="img"/>
      <p class="session-index">
        {{ `${$t('session')} ${index + 1}` }}
      </p>
      <p class="session-name" :class="{'single-line': inProgress}">
        {{ session?.name }}
      </p>
      <div class="status">
        <div v-if="session?.state === SessionStateEnum.COMPLETED" class="completed">
          <svg-illustration name="check.svg"/> {{ $t('session_complete') }}
        </div>
        <div v-else-if="inProgress" class="in-progress">
          <completed-progress v-if="completedPercent" :completed="completedPercent" class="progress"/>
          <svg-illustration name="arrow-narrow-right.svg"/>
        </div>
      </div>
    </div>
  </router-link-with-origin>
  <!-- Render as plain card if not clickable -->
  <div
    v-else
    class="session"
    :class="theme"
    aria-disabled="true"
    style="cursor: auto;"
  >
    <lazy-image :src="session!.image" :width="64" :height="64" class="img"/>
    <p class="session-index">
      {{ `${$t('session')} ${index + 1}` }}
    </p>
    <p class="session-name">
      {{ session?.name }}
    </p>
    <div class="status">
      <div v-if="session?.state === SessionStateEnum.COMPLETED" class="completed">
        <svg-illustration name="check.svg"/> {{ $t('session_complete') }}
      </div>
      <session-unlocks v-else-if="session?.timeUntilUnlockMillis" :session="session" variant="card"/>
      <div v-else-if="inProgress" class="in-progress">
        <completed-progress v-if="completedPercent" :completed="completedPercent" class="progress"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.session {
  width: var(--Spacing-spacing-13);
  height: var(--Spacing-spacing-13);
  border-radius: var(--Border-Radius-3);
  background-color: var(--Neutral-UI-neutral-ui-20);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: var(--Spacing-spacing-4) var(--Spacing-spacing-4) var(--Spacing-spacing-5) var(--Spacing-spacing-4);

  &.blue {
    // Force light mode colors for the current session card
    --Secondary-secondary-color-1: var(--Light-Scheme-Secondary-secondary-color-1);
    --Neutral-UI-neutral-ui-100: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
    background-color: var(--Secondary-secondary-color-1);
    color: var(--Neutral-UI-neutral-ui-100);
  }
  
  &.green {
    background-color: var(--Light-Scheme-Functional-wl-completed-surface);
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-10);
  }
}

.img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-bottom: 8px;

  @include screen-md {
    width: 42px;
    height: 42px;
    margin-bottom: 12px;
  }

  @include screen-lg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
  }
}

.session-index {
  @include font-body-sm;
  margin: 0;
  flex-shrink: 0;
}

.session-name {
  @include font-body-sm-bold;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  flex-shrink: 0;

  &.single-line {
    -webkit-line-clamp: 1;
    line-clamp: 1;

    @include screen-lg {
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }
  }
}

.status {
  margin-top: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.progress {
  flex: 1;
}

.completed {
  @include font-body-sm-bold;
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-1);
  min-height: var(--Spacing-spacing-7);
}

.in-progress {
  @include font-body-sm-bold;
  display: flex;
  align-items: flex-end;
  gap: var(--Spacing-spacing-6);
  width: 100%;
}


</style>
